import React, { useEffect, useMemo, useState } from 'react';
import { MutationStatus } from 'react-query';
import {
  ApiButton,
  Box,
  Grid,
  MUIDataTable,
  MUIDataTableColumnDef,
  MUIDataTableOptions,
  Pill,
  Typography,
  useWithGuardStyles,
} from 'web-library';
import { TFunction, useTranslation } from 'react-i18next';
import CircularProgress from '@mui/material/CircularProgress';
import produce, { enableES5 } from 'immer';

import { makeStyles } from 'tss-react/mui';
import { CommonErrorResponse, KeyValueResponse } from '../../../../api';
import EligibleOrganizationOptions from '../../../../components/select/EligibleOrganizationOptions';
import { CoordinatorDetails } from '../../../../api/candidate/resit/@opadmin/api-services/models';
import * as QUERIES from '../../../../api/candidate/resit/@opadmin/queries-services/queries';
import HandleErrors from '../../../../components/HandleErrors';
import { usePostSendUniqueLinkEmail } from '../../../../api-refactored/unique-link/usePostSendUniqueLinkEmail';

enableES5();

const useStyles = makeStyles()((theme) =>
({
  tableContainer: {
    marginTop: `${theme.spacing(2)} !important`,
    width: '100%'
  },
}));

/** define MUI Data Table options
 * */
const privateCenterTableOptions: MUIDataTableOptions = {
  responsive: 'standard',
  selectableRows: 'none',
  tableBodyHeight: '450px',
  elevation: 0,
  rowsPerPageOptions: [5, 10, 100],
  customToolbarSelect: () => <div />,
  print: false,
  search: false,
  viewColumns: false,
  confirmFilters: false,
  download: false,
  filter: false,
  fixedHeader: true,
};

/** define Mui DataTable Column Definition
 * */
const privateCenterTableColumns: MUIDataTableColumnDef[] = [
  {
    options: {
      searchable: true,
      sort: false,
    },
    name: 'name',
    label: 'Coordinator Name',
  },
  {
    options: {
      searchable: true,
      sort: false,
    },
    name: 'email',
    label: 'Coordinator Email',
  },
];

interface CoordinatorList extends CoordinatorDetails {
  isEmailSent: boolean;
  isSending: boolean;
}

const getCustomBodyRenderLite =
  (
    coordinators: CoordinatorList[],
    t: TFunction<'translation'>,
    privateCenterOnEmailSend: (currentDataIndex: number) => Promise<void>,
    sendEmailLinkStatus: MutationStatus
  ) =>
    (dataIndex: number) =>
    (
      <div
        style={{
          marginLeft: 8,
          display: 'flex',
          alignItems: 'center',
          minWidth: 85,
          minHeight: 26,
          justifyContent: 'flex-start',
        }}
      >
        {coordinators[dataIndex].blocked && (
          <Typography variant="body1" display="inline">
            {t('resit->>OpAdminResit.Blocked')}
          </Typography>
        )}
        {!coordinators[dataIndex].approved && (
          <Typography variant="body1" display="inline">
            {t('resit->>OpAdminResit.Not Approved')}
          </Typography>
        )}
        {!coordinators[dataIndex].blocked &&
          !coordinators[dataIndex].isSending &&
          !coordinators[dataIndex].isEmailSent &&
          coordinators[dataIndex].approved && (
            <ApiButton
              style={{ position: 'absolute' }}
              id="private-center-send-email"
              variant="text"
              onClick={async () => {
                await privateCenterOnEmailSend(dataIndex);
              }}
              size="small"
              status={sendEmailLinkStatus}
            >
              {t('resit->>OpAdminResit.Send Email')}
            </ApiButton>
          )}
        {coordinators[dataIndex].isSending && (
          <CircularProgress style={{ position: 'absolute' }} size={20} />
        )}
        {coordinators[dataIndex].isEmailSent && (
          <Pill label="Email Sent" size="S" variant="positive" />
        )}
      </div>
    );

const CoordinatorResitRegistration = () => {
  const [selectedOrganization, setSelectedOrganization] = useState<KeyValueResponse>();
  const [coordinators, setCoordinators] = useState<CoordinatorList[]>([]);
  const [responseErrors, setResponseErrors] = useState<CommonErrorResponse>();

  const { t } = useTranslation();
  const { classes } = useStyles();
  const { classes: guardClasses} = useWithGuardStyles();

  /** fetch coordinator by organization
   * */
  const { refetch, status } = QUERIES.useOopAdminCoordinatorsByOrganization(
    selectedOrganization?.code || '',
    {
      cacheTime: 0,
      refetchOnWindowFocus: false,
      enabled: false,
      retry: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      onSuccess: (data) => {
        if (data && data.length > 0) {
          setCoordinators(
            data.map((coordinator) => ({ ...coordinator, isEmailSent: false, isSending: false }))
          );
        } else {
          setResponseErrors({
            errorCode: 'empty',
            message: `Coordinators doesn't exist for ${selectedOrganization?.description}`,
          });
        }
      },
      onError: (errorResponse) => {
        if (errorResponse.response && errorResponse.response.status !== 500) {
          setResponseErrors({
            errorCode: errorResponse.response.data.code || '',
            message: errorResponse.response.data.message || '',
          });
        }
      },
    }
  );

  /** send unique email link to coordinator
   * */
  const { mutate: sendEmailLink, status: sendEmailLinkStatus } = usePostSendUniqueLinkEmail();

  /** handle organization selection
   * */
  const onOrganizationSelect = (currentOrganization: KeyValueResponse) => {
    setCoordinators([]);
    setResponseErrors(undefined);
    setSelectedOrganization(currentOrganization);
  };

  /** update coordinator state
   * * */
  const updateCoordinators = (newData: CoordinatorList, type: string) => {
    setCoordinators(
      produce(coordinators, (draftState) => {
        const index = draftState.findIndex((coordinator) => coordinator.id === newData.id);
        if (index >= 0) {
          switch (type) {
            case 'loading':
              // eslint-disable-next-line no-param-reassign
              draftState[index] = { ...draftState[index], isSending: true };
              break;
            case 'Sent':
              // eslint-disable-next-line no-param-reassign
              draftState[index] = { ...draftState[index], isSending: false, isEmailSent: true };
              break;
            default:
              // eslint-disable-next-line no-param-reassign
              draftState[index] = { ...draftState[index], isSending: false, isEmailSent: false };
              break;
          }
        }
      })
    );
  };

  /** handle private center email sending
   * **** to do after api complete ***
   * */
  /*  eslint-disable-next-line react-hooks/exhaustive-deps */
  const privateCenterOnEmailSend = async (currentDataIndex: number) => {
    updateCoordinators(coordinators[currentDataIndex], 'loading');

    sendEmailLink(
      {
        uuid: coordinators[currentDataIndex].uuid,
        userType: 'COORDINATORS',
        applicationType: 'RESIT',
        isWaiveSubjectAdminFee: false,
        isWaiveLateFee: false,
        isOfflinePayment: false,
      },
      {
        onSuccess: () => updateCoordinators(coordinators[currentDataIndex], 'Sent'),
        onError: () => updateCoordinators(coordinators[currentDataIndex], ''),
      }
    );
  };

  /** manual adding MUI Data Table Columns
   * */
  const getPrivateCenterTableColumns = useMemo<MUIDataTableColumnDef[]>(() => {
    /** private center send email action column
     * */
    const addPrivateCenterSendEmailActionColumn: MUIDataTableColumnDef = {
      options: {
        sort: false,
        searchable: false,
        customBodyRenderLite: getCustomBodyRenderLite(
          coordinators,
          t,
          privateCenterOnEmailSend,
          sendEmailLinkStatus
        ),
      },
      name: 'isBlocked',
      label: 'Send Email',
    };
    return [...privateCenterTableColumns, addPrivateCenterSendEmailActionColumn];
  }, [coordinators, privateCenterOnEmailSend, t, sendEmailLinkStatus]);

  useEffect(() => {
    if (selectedOrganization) {
      refetch();
    }
  }, [refetch, selectedOrganization]);

  return (
    <>
      <Grid container direction="column">
        <Grid xs={12}>
          <Box className={guardClasses.contentContainer} component="div">
            <EligibleOrganizationOptions
              value={selectedOrganization}
              onChange={(value) => {
                onOrganizationSelect(value);
              }}
            />
          </Box>
        </Grid>

        {responseErrors && <HandleErrors errorType="text" errorResponse={responseErrors} />}
        {!responseErrors && selectedOrganization && coordinators && coordinators.length > 0 && (
          <div className={classes.tableContainer}>
            <MUIDataTable
              isLoading={status === 'loading'}
              key="private-center"
              columns={getPrivateCenterTableColumns}
              options={privateCenterTableOptions}
              data={coordinators || []}
            />
          </div>
        )}
      </Grid>
    </>
  );
};
export default CoordinatorResitRegistration;
